import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Radio, Select, Typography, Alert, message, Spin, InputNumber } from 'antd';
import axios from 'axios';
import logger from '../../utils/logger';
import { getApiErrorMessage } from '../../utils/apiError';

const { Title, Text } = Typography;
const { Option } = Select;

const TenantCreateModalWithTiers = ({ visible, onCancel, onSuccess }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [tenantClass, setTenantClass] = useState('training');
  const [computeTier, setComputeTier] = useState('tier-1');
  const [computeTiers, setComputeTiers] = useState([]);
  const [loadingTiers, setLoadingTiers] = useState(false);

  const getAuthHeader = () => {
    const user = JSON.parse(localStorage.getItem('user'));
    return user?.access_token ? { Authorization: `Bearer ${user.access_token}` } : {};
  };

  const API_URL = import.meta.env.REACT_APP_API_URL || 'http://localhost:8000';

  // Fetch available compute tiers when modal opens
  useEffect(() => {
    if (visible && tenantClass === 'client') {
      fetchComputeTiers();
    }
  }, [visible, tenantClass]);

  const fetchComputeTiers = async () => {
    setLoadingTiers(true);
    try {
      const response = await axios.get(
        `${API_URL}/admin/tenants/compute-tiers`,
        { headers: getAuthHeader() }
      );
      setComputeTiers(response.data.tiers || []);
    } catch (error) {
      logger.error('Error fetching compute tiers:', error);
      message.error('Failed to load compute tiers');
    } finally {
      setLoadingTiers(false);
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      // Prepare request data (tenant_id auto-generated by backend)
      const requestData = {
        tenant_class: tenantClass,
        name: values.name || '',
        contact_email: values.contact_email || '',
        token_limit_millions: values.token_limit_millions || 20
      };

      // Add compute_tier for Client tenants
      if (tenantClass === 'client') {
        requestData.compute_tier = computeTier;
      }

      // Create tenant with new tier-aware endpoint
      const response = await axios.post(
        `${API_URL}/admin/tenants/create-with-tier`,
        requestData,
        { headers: getAuthHeader() }
      );

      if (response.data.success) {
        const tenantId = response.data.tenant_id;
        if (response.data.provisioning_required) {
          // Client tenant - background provisioning
          message.success(
            `Client tenant created with ID: ${tenantId}! Provisioning will take ~30 minutes. ` +
            `You can monitor progress in the tenant list.`,
            10
          );
        } else {
          // Training tenant - immediate
          message.success(`Training tenant created successfully! ID: ${tenantId}`);
        }
        form.resetFields();
        onSuccess();
      } else {
        message.error(response.data.message || 'Failed to create tenant');
      }
    } catch (error) {
      logger.error('Error creating tenant:', error);
      const errorMessage = getApiErrorMessage(error, 'Failed to create tenant. Please try again.');
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const getTierDisplayInfo = (tier) => {
    if (!tier) return null;
    return {
      cpu: `${tier.ecs_cpu / 1024} vCPU`,
      memory: `${tier.ecs_memory / 1024} GB RAM`,
      tasks: `${tier.ecs_min_tasks}-${tier.ecs_max_tasks} tasks`,
      rds: tier.rds_instance_class,
      cost: tier.monthly_cost_estimate
    };
  };

  return (
    <Modal
      title={<Title level={3}>Create New Tenant</Title>}
      open={visible}
      onCancel={onCancel}
      onOk={handleSubmit}
      okText="Create Tenant"
      cancelText="Cancel"
      confirmLoading={loading}
      width={600}
    >
      <Form
        form={form}
        layout="vertical"
      >
        {/* Tenant Class Selection */}
        <Form.Item label="Tenant Class">
          <Radio.Group
            value={tenantClass}
            onChange={(e) => setTenantClass(e.target.value)}
            style={{ width: '100%' }}
          >
            <Radio.Button value="training" style={{ width: '50%', textAlign: 'center' }}>
              <div>
                <Text strong>Training</Text>
                <br />
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  Shared Infrastructure (~$5/month)
                </Text>
              </div>
            </Radio.Button>
            <Radio.Button value="client" style={{ width: '50%', textAlign: 'center' }}>
              <div>
                <Text strong>Client</Text>
                <br />
                <Text type="secondary" style={{ fontSize: '12px' }}>
                  Dedicated Infrastructure ($58-298/month)
                </Text>
              </div>
            </Radio.Button>
          </Radio.Group>
        </Form.Item>

        {/* Info Alert */}
        {tenantClass === 'training' ? (
          <Alert
            message="Training tenants use shared infrastructure (Platform RDS, shared S3, shared ECS)"
            description="Provisioned immediately. Ideal for demos, testing, and training environments."
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />
        ) : (
          <Alert
            message="Client tenants get dedicated infrastructure"
            description="Provisioning takes ~30 minutes. Includes dedicated RDS, S3 bucket, and ECS service. Independent disaster recovery."
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}

        {/* Compute Tier Selection (Client only) */}
        {tenantClass === 'client' && (
          <Form.Item label="Compute Tier">
            {loadingTiers ? (
              <Spin />
            ) : (
              <Select
                value={computeTier}
                onChange={setComputeTier}
                style={{ width: '100%' }}
              >
                {computeTiers.map((tier) => {
                  const info = getTierDisplayInfo(tier);
                  return (
                    <Option key={tier.tier_name} value={tier.tier_name}>
                      <div>
                        <Text strong>{tier.display_name}</Text> - <Text type="success">{info.cost}/month</Text>
                        <br />
                        <Text type="secondary" style={{ fontSize: '12px' }}>
                          {info.cpu}, {info.memory}, {info.tasks}, {info.rds}
                        </Text>
                        <br />
                        <Text type="secondary" style={{ fontSize: '11px' }}>
                          {tier.description}
                        </Text>
                      </div>
                    </Option>
                  );
                })}
              </Select>
            )}
          </Form.Item>
        )}

        {/* Tenant Name (Required) */}
        <Form.Item
          label="Tenant Name"
          name="name"
          rules={[{ required: true, message: 'Please enter a tenant name' }]}
          extra="A unique tenant ID will be auto-generated (e.g., 251114A3X9)"
        >
          <Input placeholder="Acme Corporation" />
        </Form.Item>

        {/* Contact Email */}
        <Form.Item
          label="Contact Email"
          name="contact_email"
          rules={[
            { required: false },
            { type: 'email', message: 'Please enter a valid email' }
          ]}
        >
          <Input placeholder="admin@acme.com (optional)" />
        </Form.Item>

        {/* Token Limit */}
        <Form.Item
          label="Token Limit (Millions)"
          name="token_limit_millions"
          rules={[{ required: false }]}
          initialValue={20}
          tooltip="Default is 20M tokens. Enter whole numbers only (e.g., 20 = 20,000,000 tokens)"
        >
          <InputNumber
            min={1}
            max={1000}
            step={1}
            precision={0}
            addonAfter="M"
            placeholder="20"
            style={{ width: '100%' }}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default TenantCreateModalWithTiers;
